import { Card, CardBody, CardFooter, CardHeader } from '@/layers/presentation/components/ui/Card';
import { useTranslation } from '@/layers/presentation/i18n/client';
import { UserType } from '@/types/modelEnums';
import { Program } from '@/types/models';
import { Button, Chip, Link } from '@nextui-org/react';
import React, { useMemo } from 'react';
import { getProgramFinalRankScore } from './program-rank';
import ProgramNotRelevant from './ProgramNotRelevant';
import ProgramRankGauge from './ProgramRankGauge';
import ProgramTags from './ProgramTags';
import SaveProgramButton from './SaveProgramButton';

export interface ProgramCardProps {
  program: Program;
  type: UserType | 'about';
  typeUrl: string;
  onCardPress: (id: number) => void;
  selectedCancerType?: string;
}

const ProgramCard: React.FC<ProgramCardProps> = ({
  program,
  type,
  typeUrl,
  onCardPress,
  selectedCancerType,
}) => {
  const { t } = useTranslation();

  const handlePress = () => {
    onCardPress(program.id);
  };

  const isShowNotRelevantButton = type === UserType.PATIENT || type === UserType.PROVIDER;
  const isShowProgramControls = type === UserType.SPONSOR;

  const isShowEaInfo =
    program.details?.study_type !== 'EXPANDED_ACCESS' &&
    program.meta?.ea_search_clinicaltrials?.length > 0;

  if (type === UserType.PATIENT) {
    program.name = program.details?.patient_friendly?.title || program.name;
    program.lead = program.details?.patient_friendly?.lead || program.lead;
  }

  const displayConditions = useMemo(() => {
    const conditions = program.details?.conditions || [];
    const slicedConditions = conditions.slice(0, 5);

    if (selectedCancerType && !slicedConditions.includes(selectedCancerType)) {
      return [selectedCancerType, ...slicedConditions.slice(0, 4)];
    }

    return slicedConditions;
  }, [program.details?.conditions, selectedCancerType]);

  const computedRank = getProgramFinalRankScore(program);

  return (
    <Card
      as="div"
      key={program.id}
      radius="md"
      className="bg-default-100 relative"
      isPressable
      onPress={handlePress}
      data-testid="program-card"
    >
      <CardHeader>
        <div className="w-full flex flex-row gap-2 justify-between flex-wrap">
          <div className="flex flex-row items-center gap-3">
            {type !== UserType.SPONSOR && program.details?.sponsor_name && (
              <span className="font-medium sm:font-extrabold">{program.details.sponsor_name}</span>
            )}
            <ProgramTags
              program={program}
              type={type}
              isOutOfRange={program.meta?.out_of_range}
              isShowEaInfo={isShowEaInfo}
            />
          </div>
          <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs sm:text-base pt-2 sm:pt-0 -mb-[10px] sm:mb-0">
            <span className="block sm:inline text-default-400">
              {t('components:programCard.added', {
                date: program.details?.added_date,
              })}
            </span>
            <span className="block sm:inline text-default-400">
              {t('components:programCard.lastUpdated', {
                date: program.details?.updated_date,
              })}
            </span>
            <span className="block sm:inline">
              {t('components:programCard.id', { id: program.id })}
            </span>
          </div>
        </div>
      </CardHeader>
      <CardBody>
        <Link
          href={`/${typeUrl}/programs/${program.id}${program.meta?.out_of_range ? '?out_of_range=true' : ''}`}
          className="flex flex-col gap-2 items-start"
        >
          <h4 className="font-medium sm:font-normal mb-2 pb-0 text-left">{program.name}</h4>
          <p>{program.lead}</p>
        </Link>
      </CardBody>
      {(program.patient_params || type === UserType.SPONSOR) && (
        <CardFooter className="w-full">
          <div className="w-full flex flex-row gap-4 justify-between items-center flex-wrap">
            <div className="flex flex-wrap gap-4 items-center">
              {typeof computedRank === 'number' ? <ProgramRankGauge rank={computedRank} /> : null}
              {displayConditions.map((condition: string) => (
                <Chip key={condition} variant="bordered" size="sm" className="border-1">
                  {condition}
                </Chip>
              ))}
            </div>
            <div className="flex flex-row gap-4 items-center justify-end">
              {isShowNotRelevantButton && (
                <>
                  <SaveProgramButton programId={program.id} />
                  <ProgramNotRelevant program={program} typeUrl={typeUrl} />
                </>
              )}
              {isShowProgramControls && (
                <div className="flex flex-row gap-2">
                  <Button
                    as={Link}
                    href={`/${typeUrl}/programs/${program.id}/programRequests`}
                    size="sm"
                    radius="sm"
                    variant="bordered"
                    color="primary"
                  >
                    {t('components:programCard.programRequests')}
                  </Button>
                  <Button
                    as={Link}
                    href={`/${typeUrl}/programs/${program.id}/reports`}
                    size="sm"
                    radius="sm"
                    variant="bordered"
                    color="primary"
                  >
                    {t('components:programCard.reports')}
                  </Button>
                </div>
              )}
            </div>
          </div>
        </CardFooter>
      )}
    </Card>
  );
};

export default React.memo(ProgramCard);
